// ===========================================
// P&L REFERENCE RANGES — curated, never computed
// ===========================================
// Hand-curated rules of thumb commonly quoted for specialty food & beverage
// wholesale (distributor margin guides, broker/retailer norms, trade-spend
// folklore). They are NOT derived from any account's data and carry no false
// precision — each one renders with the caption below so nobody mistakes a
// reference band for their own number. When real benchmark data lands (or a
// settings UI), these constants are the single place to change.

export const BENCHMARK_CAPTION =
  'Reference ranges for specialty food & beverage wholesale — rules of thumb, not your data; edit in code until configurable.';

export type BandPosition = 'within' | 'above' | 'below';

export interface ReferenceBand {
  key: string;
  // The small line that renders under the account's number.
  rangeText: string;
  // Band edges in the metric's own unit (% or days). null = open on that side.
  lo: number | null;
  hi: number | null;
  unit: '%' | 'days';
  // Calm positioning words — never a grade, never red alarm theater.
  words: Record<BandPosition, string>;
}

export const PNL_BENCHMARKS: Record<'grossMargin' | 'deductionRate' | 'deliveryPct' | 'daysToPay', ReferenceBand> = {
  grossMargin: {
    key: 'grossMargin',
    rangeText: 'specialty food wholesale: 40–55% gross margin',
    lo: 40, hi: 55, unit: '%',
    words: { within: 'within the reference range', above: 'above the reference range', below: 'below the reference range' },
  },
  deductionRate: {
    key: 'deductionRate',
    rangeText: 'trade deductions: healthy under 2–3% of revenue',
    lo: null, hi: 3, unit: '%',
    words: { within: 'inside the healthy band', above: 'above the healthy band', below: 'inside the healthy band' },
  },
  deliveryPct: {
    key: 'deliveryPct',
    rangeText: 'delivery & fulfillment: under 8–10% of wholesale revenue',
    lo: null, hi: 10, unit: '%',
    words: { within: 'inside the reference band', above: 'above the reference band', below: 'inside the reference band' },
  },
  daysToPay: {
    key: 'daysToPay',
    rangeText: 'collections: 30–45 days is typical on Net-30 terms',
    lo: 30, hi: 45, unit: 'days',
    words: { within: 'in the typical window', above: 'slower than typical', below: 'faster than typical' },
  },
};

// Where the account's number sits relative to a band — words come from the
// band itself so one-sided bands ("under 3%") read naturally.
export function bandPosition(value: number, band: ReferenceBand): BandPosition {
  if (band.lo != null && value < band.lo) return 'below';
  if (band.hi != null && value > band.hi) return 'above';
  return 'within';
}

export function bandWording(value: number, band: ReferenceBand): string {
  return band.words[bandPosition(value, band)];
}
